import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { Button, Search, Spinner } from '@consigli/facade';
import { useDownloadFilesAsZip, useProject, useProjectId } from '@consigli/hooks';
import { Document } from '@consigli/types';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';
import { HiOutlineCloudDownload } from 'react-icons/hi';
import { HiOutlineDocumentText } from 'react-icons/hi2';
import { useParams } from 'react-router';

import { AdminBanner } from '@/components/admin-banner';
import { DocumentCard } from '@/components/document-card';
import { createRenderers } from '@/features/document-viewer/renderers';
import { resolveDocumentFileType, resolveDocumentSrc } from '@/util/document-check';
import { getDocumentPriority, sortDocumentsByType } from '@/util/document-ordering';

import { CardBasedFileDisplayProps, GroupedRecords, SectionConfig } from './types';

export const CardBasedFileDisplay: React.FC<CardBasedFileDisplayProps> = ({
  files,
  isFetching,
  isPublished,
  sections,
  showSearch = true,
  showDownloadBySection = true,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { project } = useProject(projectId);
  const [pageRecords, setPageRecords] = useState<Document[]>([]);
  const [searchText, setSearchText] = useState('');
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const { serviceName } = useParams();
  const [downloadingSection, setDownloadingSection] = useState<string | null>(null);

  const { downloadFileZip: downloadSelected, isDownloading: isDownloadingSelected } =
    useDownloadFilesAsZip(project?.name ?? 'files');

  const containerRefs = useMemo(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    sections.forEach((section) => {
      refs[section.key] = React.createRef<HTMLDivElement>();
    });
    return refs;
  }, [sections]);

  const [scrollButtonStates, setScrollButtonStates] = useState<Record<string, boolean>>(() => {
    return sections.reduce(
      (acc, section) => {
        acc[section.key] = false;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  });

  const onClickDownloadSelected = useCallback(
    async (records: Document[], sectionKey: string) => {
      setDownloadingSection(sectionKey);
      await downloadSelected(records);
      setDownloadingSection(null);
    },
    [downloadSelected],
  );

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  useEffect(() => {
    setPageRecords(files);
  }, [files]);

  const filteredRecords = useMemo(() => {
    return pageRecords.filter((record) =>
      record.name.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [pageRecords, searchText]);

  const sortedRecords = useMemo(() => {
    return sortDocumentsByType(filteredRecords);
  }, [filteredRecords]);

  const activeDocumentIndex = sortedRecords.findIndex((record) => record.id === activeDocumentId);
  const activeDocument = activeDocumentIndex >= 0 ? sortedRecords[activeDocumentIndex] : null;

  const renderers = useMemo(() => createRenderers(), []);

  const groupedRecords = useMemo(() => {
    const initialValue: GroupedRecords = sections.reduce((acc, section) => {
      acc[section.key] = [];
      return acc;
    }, {} as GroupedRecords);

    return sortedRecords.reduce((acc, record) => {
      const matchingSection = sections.find((section) => {
        if (section.groupingFn) {
          return section.groupingFn(record);
        } else if (section.orderPriority !== undefined) {
          const priority = getDocumentPriority(record);
          return section.orderPriority === priority;
        }
        return false;
      });

      if (matchingSection) {
        acc[matchingSection.key].push(record);
      } else {
        const fallbackKey = sections.find((s) => s.key === 'others')?.key || sections[0]?.key;
        if (fallbackKey && acc[fallbackKey]) {
          acc[fallbackKey].push(record);
        }
      }

      return acc;
    }, initialValue);
  }, [sortedRecords, sections]);

  const getOnClickHandler = useCallback((record: Document) => {
    return () => {
      setActiveDocumentId(record.id);
    };
  }, []);

  const renderDocumentCard = (record: Document, imageSource: string) => (
    <DocumentCard
      key={record.id}
      record={record}
      imageSource={imageSource}
      onClick={getOnClickHandler(record)}
    />
  );

  const scrollContainer = (
    containerRef: React.RefObject<HTMLDivElement>,
    direction: 'left' | 'right',
  ) => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const visibleItemWidth = 240;
      const visibleItems = Math.floor(containerWidth / visibleItemWidth);
      const currentPosition = containerRef.current.scrollLeft;
      let targetPosition;

      if (direction === 'left') {
        targetPosition = Math.max(0, currentPosition - visibleItems * visibleItemWidth);
      } else {
        targetPosition = currentPosition + visibleItems * visibleItemWidth;
      }

      containerRef.current.scrollTo({
        left: targetPosition,
        behavior: 'smooth',
      });
    }
  };

  const checkOverflow = useCallback(
    (containerRef: React.RefObject<HTMLDivElement>, sectionKey: string) => {
      if (containerRef.current) {
        const hasOverflow = containerRef.current.scrollWidth > containerRef.current.clientWidth;
        setScrollButtonStates((prev) => ({
          ...prev,
          [sectionKey]: hasOverflow,
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const checkAllOverflows = () => {
      sections.forEach((section) => {
        checkOverflow(containerRefs[section.key], section.key);
      });
    };

    checkAllOverflows();
    window.addEventListener('resize', checkAllOverflows);

    return () => {
      window.removeEventListener('resize', checkAllOverflows);
    };
  }, [checkOverflow, containerRefs, groupedRecords, sections]);

  const renderSection = (section: SectionConfig) => {
    const records = groupedRecords[section.key];

    if (records.length === 0) {
      return null;
    }

    const containerRef = containerRefs[section.key];
    const showScrollButtons = scrollButtonStates[section.key];
    const isDownloading = downloadingSection === section.key;

    let descriptionText = t(section.descriptionKey);
    if (section.interpolation && serviceName) {
      const interpolationValues = Object.entries(section.interpolation).reduce(
        (acc, [key, value]) => {
          if (typeof value === 'string' && value.includes('.')) {
            acc[key] = t(`${value}.${serviceName}`);
          } else {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      descriptionText = t(section.descriptionKey, interpolationValues) as string;
    }

    return (
      <div key={section.key} className="2xl:mb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between h-8 2xl:h-10">
          <div className="flex items-center gap-x-4">
            <h3 className="2xl:text-xl text-sm">{t(section.translationKey)}</h3>
            <div className="mx-4 text-night-light-5 font-normal 2xl:text-base text-sm">
              {descriptionText}
            </div>
          </div>
          {showDownloadBySection && (
            <div>
              <button
                disabled={
                  (downloadingSection !== null && downloadingSection !== section.key) ||
                  isDownloadingSelected
                }
                onClick={() => onClickDownloadSelected(records, section.key)}
                className={clsx(
                  'hover:bg-day-light-3 rounded-full p-2 flex items-center gap-2',
                  'w-auto h-10 px-4 cursor-pointer text-night-light-5',
                  isDownloading && 'transition-colors duration-200',
                )}
                aria-label={
                  isDownloading ? t('engineering.downloading') : t('engineering.download-files')
                }
              >
                <span className="text-sm">
                  {isDownloading ? t('engineering.downloading') : t('engineering.download-all')}
                </span>
                {isDownloading ? <Spinner size="xsmall" /> : <HiOutlineCloudDownload size={20} />}
              </button>
            </div>
          )}
        </div>
        <div className="relative w-full">
          {showScrollButtons && (
            <Button
              tertiary
              rounded
              className="font-light hover:bg-day-light-3 hover:text-night-dark-1 focus:bg-[#33415587] focus:text-white absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-[#33415587] rounded-full p-2 shadow-md text-white"
              onClick={() => scrollContainer(containerRef, 'left')}
            >
              <FaChevronLeft />
            </Button>
          )}
          <div
            ref={containerRef}
            className="pl-4 flex items-center justify-start gap-x-8 overflow-x-auto no-scrollbar scroll-smooth pr-4"
          >
            {records.map((record) => renderDocumentCard(record, section.image))}
          </div>
          {showScrollButtons && (
            <Button
              tertiary
              rounded
              className="font-light hover:bg-day-light-3 hover:text-night-dark-1 focus:bg-[#33415587] focus:text-white absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-[#33415587] rounded-full p-2 shadow-md text-white"
              onClick={() => scrollContainer(containerRef, 'right')}
            >
              <FaChevronRight />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full">
      <div className="px-4 sm:px-6 md:px-8 w-full">
        {isFetching ? (
          <Spinner />
        ) : pageRecords.length === 0 ? (
          <div className="flex items-center justify-center w-full min-h-[calc(100vh-200px)]">
            <div className="text-center">
              <HiOutlineDocumentText className="mx-auto h-16 w-16 text-day-neutral-subtle mb-4" />
              <h3 className="text-xl font-medium text-day-neutral-subtle mb-2">
                {t('document-list.no-documents-match-filter-title')}
              </h3>
              <p className="text-night-light-5 max-w-md">
                {t('document-list.no-documents-match-filter-description')}
              </p>
            </div>
          </div>
        ) : (
          <>
            {showSearch && (
              <div className="flex flex-row justify-between items-center w-full 2xl:py-8 py-2">
                <Search
                  text={searchText}
                  onChange={handleSearch}
                  placeholder={t('project-list.search-files')}
                />
                <AdminBanner published={isPublished} />
              </div>
            )}

            {sections.map((section) => renderSection(section))}

            {activeDocument && (
              <DocumentViewer
                open={activeDocumentIndex >= 0}
                onOpenChange={(details) => {
                  if (!details.open) setActiveDocumentId(null);
                }}
                src={resolveDocumentSrc(activeDocument)}
                fileType={resolveDocumentFileType(activeDocument)}
                fileName={activeDocument.name}
                title={activeDocument.name}
                showSidebar={false}
                showNavigationActions={sortedRecords.length > 1}
                previousDisabled={activeDocumentIndex <= 0}
                nextDisabled={activeDocumentIndex >= sortedRecords.length - 1}
                onPrevious={() =>
                  setActiveDocumentId(sortedRecords[activeDocumentIndex - 1]?.id ?? null)
                }
                onNext={() =>
                  setActiveDocumentId(sortedRecords[activeDocumentIndex + 1]?.id ?? null)
                }
                renderers={renderers}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
